import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import api from '../services/api';
import MovieCard from './MovieCard';
import FilterSortControls from './FilterSortControls';
import KriticalWatching from './KriticalWatching';
import NewKriticalMovies from './NewKriticalMovies';
import TopKriticsHome from './TopKriticsHome';
import { debounce } from 'lodash';

const Container = styled.div`
  padding: 20px;
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.border};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

const Loader = styled.div`
  text-align: center;
  padding: 20px;
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.error};
  text-align: center;
`;

const SESSION_STORAGE_KEY = 'movieSearchState';

function MovieSearch() {
  const [query, setQuery] = useState('');
  const [movies, setMovies] = useState([]);
  const [userRatings, setUserRatings] = useState(new Map());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [minMovies, setMinMovies] = useState(15);

  const [filters, setFilters] = useState({
    unrankedOnly: false,
    includeAdult: false,
  });
  const [sortBy, setSortBy] = useState('popularity.desc');
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [lastRatingUpdate, setLastRatingUpdate] = useState(Date.now());

  const observer = useRef();
  const isInitialLoad = useRef(true);

  const user = useMemo(() => JSON.parse(localStorage.getItem('user')), []);

  const lastMovieElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && page < totalPages) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, page, totalPages]);

  const fetchMovies = useCallback(async (searchQuery, pageNum, includeAdult, sort, shouldReset) => {
    setLoading(true);
    setError(null);
    try {
      const data = await (searchQuery
        ? api.searchMovies(searchQuery, pageNum, includeAdult)
        : api.getPopularMovies(pageNum, includeAdult, sort));

      setMovies(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMovies = data.results.filter(m => !existingIds.has(m.id));
        return shouldReset ? data.results : [...prev, ...newMovies];
      });
      setTotalPages(data.total_pages);
    } catch (err) {
      setError('Failed to fetch movies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (savedState) {
      const { query, movies, page, totalPages, minMovies, filters, sortBy, scrollPosition } = JSON.parse(savedState);
      setQuery(query);
      setMovies(movies);
      setPage(page);
      setTotalPages(totalPages);
      setMinMovies(minMovies);
      setFilters(filters);
      setSortBy(sortBy);
      setTimeout(() => window.scrollTo(0, scrollPosition), 100);
    }
    isInitialLoad.current = false;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const stateToSave = {
        query, movies, page, totalPages, minMovies, filters, sortBy,
        scrollPosition: window.scrollY
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [query, movies, page, totalPages, minMovies, filters, sortBy]);

  useEffect(() => {
    const fetchInitialRatings = async () => {
      if (user) {
        try {
          const interactions = await api.getUserRatings(user.id);
          const interactionMap = new Map();
          interactions.forEach(i => {
            interactionMap.set(i.tmdb_id, {
              rating: i.rating,
              is_dismissed: i.is_dismissed === 1,
              is_wanna_watch: i.is_wanna_watch === 1
            });
          });
          setUserRatings(interactionMap);
        } catch (err) { console.error("Failed to fetch user interactions:", err); }
      }
    };
    fetchInitialRatings();
  }, [user]);

  const debouncedFetch = useMemo(() => debounce((...args) => fetchMovies(...args), 300), [fetchMovies]);

  useEffect(() => {
    if (isInitialLoad.current) return;
    debouncedFetch(query, 1, filters.includeAdult, sortBy, true);
  }, [query, filters, sortBy, debouncedFetch]);

  useEffect(() => {
    if (!isInitialLoad.current && page > 1) {
      fetchMovies(query, page, filters.includeAdult, sortBy, false);
    }
  }, [page, query, filters.includeAdult, sortBy, fetchMovies]);

  const handleRatingUpdate = (movieId, newRating, interaction = {}) => {
    const previousRating = userRatings.get(movieId)?.rating || 0;
    const isRankingChange = (newRating > 0) || (previousRating > 0 && newRating === 0);

    setUserRatings(prev => {
      const next = new Map(prev);
      const current = next.get(movieId) || {};
      next.set(movieId, {
        ...current,
        rating: newRating,
        is_dismissed: interaction.is_dismissed ?? current.is_dismissed ?? false,
        is_wanna_watch: interaction.is_wanna_watch ?? current.is_wanna_watch ?? false
      });
      return next;
    });

    setLastUpdate(Date.now());
    if (isRankingChange) {
      setLastRatingUpdate(Date.now());
    }

    // Always remove from grid if rated (if unrankedOnly), dismissed, or added to Wanna Watch
    if ((filters.unrankedOnly && newRating > 0) || interaction.is_dismissed || interaction.is_wanna_watch) {
      setMovies(prev => prev.filter(m => m.id !== movieId));
    }
  };

  const clearAll = () => {
    setFilters({ unrankedOnly: false, includeAdult: false });
    setSortBy('popularity.desc');
    setQuery('');
    setMinMovies(15);
    setMovies([]);
    setPage(1);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  };

  const sortedAndFilteredMovies = useMemo(() => {
    let processedMovies = [...movies];
    // Filter out dismissed and wanna watch movies
    processedMovies = processedMovies.filter(movie => {
      const interaction = userRatings.get(movie.id);
      return !interaction || (!interaction.is_dismissed && !interaction.is_wanna_watch);
    });

    if (filters.unrankedOnly) {
      processedMovies = processedMovies.filter(movie => {
        const interaction = userRatings.get(movie.id);
        return !interaction || interaction.rating === 0;
      });
    }
    if (query && sortBy !== 'popularity.desc') {
      processedMovies.sort((a, b) => {
        const [field, order] = sortBy.split('.');
        let valA, valB;
        if (field === 'release_date') {
          valA = new Date(a.release_date);
          valB = new Date(b.release_date);
        } else {
          valA = a.title.toLowerCase();
          valB = b.title.toLowerCase();
        }
        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return processedMovies;
  }, [movies, query, filters.unrankedOnly, sortBy, userRatings]);

  return (
    <Container>
      <NewKriticalMovies
        user={user}
        onRatingUpdate={handleRatingUpdate}
        lastUpdate={lastUpdate}
      />
      <KriticalWatching
        user={user}
        onRatingUpdate={handleRatingUpdate}
        lastUpdate={lastUpdate}
        titleLink="/kritical-watching"
      />
      <TopKriticsHome user={user} lastUpdate={lastRatingUpdate} />
      <FilterSortControls
        filters={filters}
        setFilters={setFilters}
        sortBy={sortBy}
        setSortBy={setSortBy}
        clearAll={clearAll}
      />
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search for a movie..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </SearchContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Grid>
        {sortedAndFilteredMovies.map((movie, index) => {
          const interaction = userRatings.get(movie.id) || {};
          const movieWithFlags = { ...movie, is_wanna_watch: interaction.is_wanna_watch };

          if (sortedAndFilteredMovies.length === index + 1) {
            return (
              <div ref={lastMovieElementRef} key={movie.id}>
                <MovieCard
                  movie={movieWithFlags}
                  userRating={interaction.rating || 0}
                  onRatingUpdate={handleRatingUpdate}
                />
              </div>
            );
          } else {
            return (
              <MovieCard
                key={movie.id}
                movie={movieWithFlags}
                userRating={interaction.rating || 0}
                onRatingUpdate={handleRatingUpdate}
              />
            );
          }
        })}
      </Grid>

      {loading && <Loader>Loading...</Loader>}
    </Container>
  );
}

export default MovieSearch;
